import React, { useEffect, useMemo, useState } from 'react'
import { IBaseClockingItem, IEmployee } from '@cbgms/api'
import { IClockingAction } from '@cbgms/api/modules/clocking-actions'
import { Icon, Table } from 'semantic-ui-react'
import Timer from './Timer'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { IRootReducerState } from 'state/rootReducer'
import { currentClockingAction, workorderClockingActions } from './state/actions'
import { useQuery } from '@tanstack/react-query'
import { Loader } from '@carsys/ui'
import { Box, Collapse, IconButton, Stack, Typography } from '@mui/material'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import SubdirectoryArrowIcon from '../../assets/svg/SubdirectoryArrow.svg'
import StopIcon from '@mui/icons-material/Stop'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { IClockedTime, OTHER_REPAIRS_KEY, aggregateTimePerClockingItem, emptyClockedTime } from './workorder-activity-clocking'

interface IWorkorderClockingRowProps {
  clockIn: (workorderUUID: string, workorderClockingItemUUID?: string) => void
  clockOut: () => void
  workorder: IBaseClockingItem
  workorderClockingItems: IBaseClockingItem[]
  NameComponent?: React.FC<{ name: string; url?: string; brand?: string }>
  currentAction: IClockingAction | null
}

interface ISubClockingRowProps {
  name: string
  isActive: boolean
  seconds: number
  onClock: () => void
  testid: string
}

const subCellStyle = { borderTop: 0, borderBottom: 0 }

const WorkorderClockingRow = ({
  clockIn,
  clockOut,
  workorder,
  workorderClockingItems,
  NameComponent,
  currentAction
}: IWorkorderClockingRowProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(true)
  const [pendingActiveKey, setPendingActiveKey] = useState<string | null>(null)

  const currentEmployee = useSelector<IRootReducerState, IEmployee>(state => state.auth.employee)

  const {
    data: result,
    isLoading,
    refetch: reload
  } = useQuery(['WorkorderClockingRow', workorder.UUID], async () => {
    const [currentResponse, workorderClocksResponse] = await Promise.all([
      dispatch(currentClockingAction()),
      dispatch(workorderClockingActions(workorder.UUID))
    ])

    return aggregateTimePerClockingItem(workorderClocksResponse.Data, currentResponse.Data.ServerTime, currentEmployee.UUID)
  })

  useEffect(() => {
    reload()
  }, [currentAction?.UUID, currentAction?.WorkorderUUID])

  useEffect(() => {
    if (!currentAction || currentAction.WorkorderUUID !== workorder.UUID) {
      setPendingActiveKey(null)
    }
  }, [currentAction?.UUID, currentAction?.WorkorderUUID])

  const total = useMemo<IClockedTime>(() => {
    if (!result) {
      return emptyClockedTime
    }

    return { seconds: result.totalSeconds, isActive: result.totalIsActive }
  }, [result])

  const getClockedTime = (key: string): IClockedTime => result?.perItem?.[key] ?? emptyClockedTime

  const handleClockAction = (isActive: boolean, key: string) => {
    if (isActive) {
      setPendingActiveKey(null)
      clockOut()
    } else {
      setPendingActiveKey(key)
      clockIn(workorder.UUID, key === OTHER_REPAIRS_KEY ? undefined : key)
    }
  }

  const activeKey = useMemo<string | null>(() => {
    const isThisWorkorderActive = !!currentAction && currentAction.WorkorderUUID === workorder.UUID
    if (!isThisWorkorderActive) {
      return null
    }

    const openBucket = result && Object.entries(result.perItem).find(([, value]) => value.isActive)
    if (openBucket) {
      return openBucket[0]
    }

    return pendingActiveKey
  }, [currentAction?.UUID, currentAction?.WorkorderUUID, result, pendingActiveKey])

  const isWorkorderActive = activeKey !== null
  const parentSeconds = total.seconds

  const renderClockingRow = (key: string, name: string, testid: string) => {
    const bucket = getClockedTime(key)
    const isActive = activeKey === key
    const seconds = bucket.seconds

    return (
      <SubClockingRow
        key={testid}
        name={name}
        isActive={isActive}
        seconds={seconds}
        onClock={() => handleClockAction(isActive, key)}
        testid={testid}
      />
    )
  }

  return (
    <>
      <Table.Row>
        <Table.Cell colSpan={3} style={{ ...subCellStyle, padding: '4px' }}>
          <Stack direction='row' alignItems='center'>
            <Stack direction='row' alignItems='center' spacing={1} sx={{ flex: 1, minWidth: 0 }}>
              <IconButton
                data-testid={`timeregistration-workorder-toggle-${workorder.UUID}`}
                size='small'
                disableRipple
                onClick={() => setIsOpen(open => !open)}
                sx={{ padding: 0 }}
              >
                {isOpen ? <KeyboardArrowUpIcon fontSize='small' /> : <KeyboardArrowDownIcon fontSize='small' />}
              </IconButton>
              {NameComponent ? (
                <NameComponent name={workorder.Name} url={workorder.UrlToPage} brand={workorder.Brand} />
              ) : (
                <Typography variant='body1'>{workorder.Name}</Typography>
              )}
            </Stack>
            <Box sx={{ width: '120px', flexShrink: 0, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
              {isLoading ? (
                <Loader loading />
              ) : isWorkorderActive ? (
                <Timer workedTime={parentSeconds} isInActions />
              ) : (
                <Stack direction='row' alignItems='center' spacing={0.5}>
                  <Icon name='clock outline' style={{ margin: 0, lineHeight: 1 }} />
                  <span>{moment.duration(total.seconds, 's').format('HH:mm:ss', { trim: false })}</span>
                </Stack>
              )}
            </Box>
            <Box sx={{ width: '44px', flexShrink: 0 }} />
          </Stack>
        </Table.Cell>
      </Table.Row>
      <Table.Row>
        <Table.Cell colSpan={3} style={{ borderTop: 0, padding: 0, borderBottom: 'none' }}>
          <Collapse in={isOpen} unmountOnExit>
            <Table basic='very' compact style={{ margin: 0 }}>
              <Table.Body>
                {renderClockingRow(
                  OTHER_REPAIRS_KEY,
                  t('TimeRegistration.OtherRepairs'),
                  `timeregistration-workorder-${workorder.UUID}-otherrepairs`
                )}
                {workorderClockingItems.map(item =>
                  renderClockingRow(item.UUID, item.Name, `timeregistration-workorder-${workorder.UUID}-item-${item.UUID}`)
                )}
              </Table.Body>
            </Table>
          </Collapse>
          <Box sx={{ borderBottom: '1px solid #2224261a', mb: 1, mt: 1 }} />
        </Table.Cell>
      </Table.Row>
    </>
  )
}

const SubClockingRow = ({ name, isActive, seconds, onClock, testid }: ISubClockingRowProps) => {
  return (
    <Table.Row>
      <Table.Cell colSpan={3} style={{ ...subCellStyle, padding: '4px' }}>
        <Stack direction='row' alignItems='center'>
          <Stack direction='row' alignItems='center' spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ width: 20, ml: '2px', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
              <SubdirectoryArrowIcon width={14} height={14} />
            </Box>
            <Typography variant='body1'>{name}</Typography>
          </Stack>
          <Box sx={{ width: '120px', flexShrink: 0, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
            {isActive ? (
              <Timer workedTime={seconds} isInActions />
            ) : (
              <Stack direction='row' alignItems='center' spacing={0.5}>
                <Icon name='clock outline' style={{ margin: 0, lineHeight: 1 }} />
                <span>{moment.duration(seconds, 's').format('HH:mm:ss', { trim: false })}</span>
              </Stack>
            )}
          </Box>
          <Box sx={{ width: '44px', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
            <IconButton
              data-testid={`${testid}-toggle`}
              color='primary'
              size='small'
              onClick={onClock}
              sx={{ border: '1px solid', borderColor: 'primary.main', padding: '5px' }}
            >
              {isActive ? <StopIcon fontSize='small' /> : <PlayArrowIcon fontSize='small' />}
            </IconButton>
          </Box>
        </Stack>
      </Table.Cell>
    </Table.Row>
  )
}

export default WorkorderClockingRow
